import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IconBranchFill, IconProjectFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';

import { track } from '../../global/tracking';
import { FragmentType, graphql, useFragment } from '../../graphql/__generated__';
import { BuildStatus } from '../../graphql/__generated__/graphql';
import { urls } from '../../routes';
import ConditionalWrapper from '../common/ConditionalWrapper';
import ProgressBar from '../common/ProgressBar';
import VisualStatus from '../common/VisualStatus';
import { getVisualProgressData } from '../common/utils';
import Styles from './BuildsItem.styles';
import DateLabel from '../common/DateLabel';
import Truncate from '../common/Truncate';
import { useVisualDispatch } from '../../reduxHooks';
import { thunks } from '../../ducks/filterBar';

export const BuildsViewBuildFragment = graphql(`
  fragment BuildsViewBuildFragment on Build {
    id
    name
    status
    branch
    createdByUser {
      id,
      username
    }
    createdAt
    project
    diffs {
      totalCount
    }
    approved: diffCount(status: APPROVED)
    unapproved: diffCount(status: UNAPPROVED)
    rejected: diffCount(status: REJECTED)
    equal: diffCount(status: EQUAL)
  }
`);

type Props = {
  build: FragmentType<typeof BuildsViewBuildFragment>
}

const VIEWABLE_STATUSES = [
  BuildStatus.Approved,
  BuildStatus.Unapproved,
  BuildStatus.Equal,
  BuildStatus.Rejected,
  BuildStatus.Running,
  BuildStatus.Errored,
];

const BuildsItem: React.FC<Props> = ({ build }) => {
  const data = useFragment(BuildsViewBuildFragment, build);
  const progressBarData = useMemo(() => getVisualProgressData(data), [data]);
  const projectRef = useRef<HTMLDivElement>(null);
  const isRunning = data.status === BuildStatus.Running;
  const dispatch = useVisualDispatch();

  const onUserClick = (event: React.MouseEvent) => {
    event.preventDefault();
    void dispatch(thunks.selectOwnerByUserId(data.createdByUser.id));
  };

  const isViewable = VIEWABLE_STATUSES.includes(data.status);

  return (
    <div className={Styles.item}>
      <div className="border-0 border-b border-gray-200 border-solid">
        <ConditionalWrapper
          condition={isViewable}
          wrapper={(content) => (
            <Link
              className="block hover:no-underline hover:bg-gray-50"
              onClick={track.buildClicked}
              to={urls.getDiff(build.id)}
            >
              <>{content}</>
            </Link>
          )}
        >
          <div className="flex flex-grow items-center gap-2 ml-2 mr-4 py-2 overflow-hidden">
            <div className="flex flex-grow gap-2 items-center flex-shrink min-w-0">
              <div className="w-14 flex-shrink-0 mr-2">
                <VisualStatus buildType={data.status} />
              </div>
              <div className="flex flex-col flex-1 w-2/5 min-w-0">
                <Truncate
                  className={classNames('font-semibold', { 'text-high-emphasis hover:underline': isViewable, 'text-medium-emphasis': !isViewable })}
                  text={data.name}
                  variant="body-large"
                />
                <div className="flex items-center gap-0.5 self-start text-medium-emphasis">
                  <DateLabel data={data.createdAt} />
                  {data.createdByUser?.username && (
                    <>
                      <Typography variant="body">
                        by
                      </Typography>
                      <Link
                        className="text-gray-600 hover:text-blue-700"
                        onClick={onUserClick}
                        to={urls.getBuilds()}
                      >
                        {`@${data.createdByUser.username}`}
                      </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="flex w-2/5 text-medium-emphasis fill-current">
                <div
                  ref={projectRef}
                  className="flex items-center gap-1 max-w-19 min-w-0"
                >
                  {data.project && (
                    <>
                      <IconProjectFill className="h-2 w-2 flex-shrink-0" />
                      <Truncate
                        className="mr-2"
                        text={data.project}
                        variant="body"
                      />
                    </>
                  )}
                </div>
                <div
                  className="flex items-center gap-1 min-w-0 flex-1"
                  style={{ maxWidth: `calc(100% - ${Number(projectRef.current?.offsetWidth)}px)` }}
                >
                  {data.branch && (
                    <>
                      <IconBranchFill className="h-2 w-2 flex-shrink-0" />
                      <Truncate
                        text={data.branch}
                        variant="body"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="hidden lg:flex flex-shrink-0 items-center w-16 xl:w-24">
              {!isRunning && (
                <ProgressBar
                  data={progressBarData}
                />
              )}
            </div>

          </div>
        </ConditionalWrapper>
      </div>

    </div>

  );
};

export default BuildsItem;
